export default function GaussSolverExplanation() {
  return (
    <div className="w-full p-1 text-xs text-gray-800 text-justify leading-relaxed hyphens-auto md:text-sm">
      <p>
        <strong>Goal:</strong> Solve a system of linear equations <code>Ax = b</code>, where:
      </p>
      <ul className="list-disc pl-6 my-2">
        <li><code>A ∈ ℝⁿˣⁿ</code> is a square matrix</li>
        <li><code>b ∈ ℝⁿ</code> is the right-hand side vector</li>
        <li><code>x ∈ ℝⁿ</code> is the unknown solution vector</li>
      </ul>
      <p className="mt-4 font-semibold">Mathematical explanation</p>
      The algorithm transforms the matrix <code>A</code> into an upper triangular form using row operations.
      It only performs row swaps ("pivoting") if a zero (or near-zero <code> &lt;10<sup>−12</sup></code>) pivot is encountered.
      <ul className="list-disc pl-5 space-y-2 mb-4 mt-4">
        <li>
          <strong>1. Sanity checks:</strong> The method first checks whether the system has a unique solution:
          <ul className="list-disc pl-6 mt-1">
            <li>If <code>rank(A) &lt; rank([A | b])</code>: the system is inconsistent (no solution)</li>
            <li>If <code>rank(A) = rank([A | b]) &lt; n</code>: infinitely many solutions</li>
            <li>If <code>rank(A) = rank([A | b]) = n</code>: exactly one solution</li>
          </ul>
        </li>

        <li>
          <strong>2. Conditional pivoting:</strong> If the current diagonal element (pivot) is zero or nearly zero,
          a row below with the largest absolute value in that column is swapped upward to avoid division by zero.
          This is done <em>only if necessary</em> — not automatically every step.
        </li>

        <li>
          <strong>3. Normalize pivot row:</strong> The current row is scaled so that the pivot becomes 1.
        </li>

        <li>
          <strong>4. Eliminate below:</strong> Rows below are updated by subtracting suitable multiples of the pivot row,
          eliminating entries below the pivot.
        </li>

        <li>
          <strong>5. Back substitution:</strong> Once the matrix is upper triangular, the solution is computed from bottom to top.
        </li>
      </ul>

      <p className="mt-4 font-semibold">Input format</p>
      Enter the matrix <code>A</code> as a 2D array and the vector <code>b</code> as a 1D array.
      <pre className="bg-white p-2 border border-gray-200 rounded text-xs mt-1 mb-2">
        A: <code>[[1, 1, -1, 1],  [1, -2, 1, 2], <br />    [2, -1, 2, -1], [3, 2, -1, 1]]</code> <br />
        b: <code>[2, 6, 2, 4]</code>
      </pre>
      <p className="mt-4 font-semibold">Output format</p>
      <ul className="list-disc pl-5 mb-4">
        <li>Each row operation (swap, normalize, subtract) is recorded in a detailed log</li>
        <li>Back-substitution is also included in the step log with intermediate vectors</li>
        <li>For large matrices (n &gt; 5), the log is written in a downloadable logfiile</li>
      </ul>
    </div>
  );
}

